/**
 * WCAG 2.1 color contrast utilities.
 *
 * Computes relative luminance and contrast ratios between two colors
 * to verify WCAG AA compliance.
 */

function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace(/^#/, '');
  if (cleaned.length === 3) {
    const r = parseInt(cleaned[0]! + cleaned[0]!, 16);
    const g = parseInt(cleaned[1]! + cleaned[1]!, 16);
    const b = parseInt(cleaned[2]! + cleaned[2]!, 16);
    return { r, g, b };
  }
  if (cleaned.length === 6) {
    const r = parseInt(cleaned.slice(0, 2), 16);
    const g = parseInt(cleaned.slice(2, 4), 16);
    const b = parseInt(cleaned.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
}

function srgbChannelToLinear(channel: number): number {
  const normalized = channel / 255;
  return normalized <= 0.04045
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

function getRelativeLuminance(hex: string): number | null {
  const rgb = parseHexColor(hex);
  if (!rgb) {
    return null;
  }
  const r = srgbChannelToLinear(rgb.r);
  const g = srgbChannelToLinear(rgb.g);
  const b = srgbChannelToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Returns the WCAG 2.1 contrast ratio between two hex colors.
 * Result is between 1 and 21.
 * Returns `null` if either color is unparseable.
 */
export function getContrastRatio(color1: string, color2: string): number | null {
  const l1 = getRelativeLuminance(color1);
  const l2 = getRelativeLuminance(color2);
  if (l1 === null || l2 === null) {
    return null;
  }
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Returns true if the text-on-background pair meets WCAG AA.
 * Normal text: ratio >= 4.5. Large text (>=18px bold or >=24px): ratio >= 3.0.
 */
export function meetsWcagAA(
  textColor: string,
  bgColor: string,
  isLargeText = false
): boolean {
  const ratio = getContrastRatio(textColor, bgColor);
  if (ratio === null) {
    return false;
  }
  return isLargeText ? ratio >= 3.0 : ratio >= 4.5;
}

/**
 * Checks a text color against multiple background colors.
 * Returns an array of results, one per background.
 */
export function checkContrastAgainstBackgrounds(
  textColor: string,
  backgroundColors: string[],
  isLargeText = false
): Array<{ bgColor: string; ratio: number | null; passes: boolean }> {
  return backgroundColors.map((bgColor) => {
    const ratio = getContrastRatio(textColor, bgColor);
    return {
      bgColor,
      ratio,
      passes: ratio !== null && (isLargeText ? ratio >= 3.0 : ratio >= 4.5),
    };
  });
}

/**
 * Picks a readable foreground for `bgHex`, biased toward white (today's
 * universal default) so existing accent colors don't flip unnecessarily:
 * white is kept whenever it clears the large-text AA floor (3.0) — the
 * practical "not actually hard to read" bar for the bold header/label text
 * these surfaces use — and only the two hardest cases fall through to
 * comparing ratios: an unparseable `bgHex`, or a light accent where white
 * genuinely fails.
 */
export function getReadableTextColor(bgHex: string, darkColor = '#40405D'): string {
  if (meetsWcagAA('#FFFFFF', bgHex, true)) {
    return '#FFFFFF';
  }
  const contrastWithWhite = getContrastRatio('#FFFFFF', bgHex);
  const contrastWithDark = getContrastRatio(darkColor, bgHex);
  if (contrastWithWhite === null || contrastWithDark === null) {
    return '#FFFFFF';
  }
  return contrastWithDark > contrastWithWhite ? darkColor : '#FFFFFF';
}
